
import { CategoryApi } from "@/api/CategoryApi";
import { EDITOR_INPUT, FILE_UPLOAD_INPUT, SELECT_INPUT_TYPE, VALIDATION_ERROR_MESSAGE, Yup } from "@mavichpe/dynamic-forms";
import { MEASURE_UNITS, PRODUCT_TYPES, PRODUCT_VISIBILITIES, TAXES_PERCENTAGES, TAXES_TYPES } from "@utils/Constants";
import { FormFileServer } from "@utils/FormFileServer";
import { CABYS_PRODUCT_TYPE_MISMATCH_MESSAGE, isCabysCoherentWithProductType } from "@utils/Cabys";
import { OpenCabysSearchPubSub } from "@utils/pubsubs/PubSub";
import { Button } from "reactstrap";

const ProductFormConfig = {
    sections: [
        {
            hideBorderBottom: true,
            "fields": [
                [
                    {
                        "type": SELECT_INPUT_TYPE,
                        "name": "categoryId",
                        "label": "Categoria",
                        "optionsFunction": () => CategoryApi.optionsList(),
                        "isHorizontal": true,
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 4
                        },
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
                    },
                ],
                [
                    {
                        "type": "text",
                        "name": "codigo",
                        "label": "Codigo",
                        "isHorizontal": true,
                        "sizing": {
                            "sm": 4
                        },
                        "labelSizing": {
                            "sm": 2
                        },
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
                    },
                ],
                [
                    {
                        "type": "text",
                        "name": "codigo_cabys",
                        "label": "Codigo Cabys",
                        "isHorizontal": true,
                        "maxlength": 13,
                        "sizing": {
                            "sm": 4
                        },
                        "labelSizing": {
                            "sm": 2
                        },
                        // El tipo se lee del primer digito del CAByS, asi que la combinacion
                        // tiene que validarse en los dos campos: Formik solo pinta el error en
                        // el que el usuario toco.
                        "validation": Yup.string()
                            .required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR)
                            .test(
                                "cabys-tipo-producto",
                                CABYS_PRODUCT_TYPE_MISMATCH_MESSAGE,
                                function (value) {
                                    return isCabysCoherentWithProductType(value, this.parent?.tipo_producto);
                                }
                            ),
                        "suffixButton": true,
                        "suffix": <Button
                            className="rounded-0"
                            block
                            color="primary"
                            type="button"
                            onClick={() => OpenCabysSearchPubSub.publish({ openOffCanvas: true })}>
                            Buscar
                        </Button>
                    },
                ],
                [
                    {
                        "type": "text",
                        "name": "descripcion_fact",
                        "label": "Descripcion de Factura",
                        "isHorizontal": true,
                        "labelSizing": {
                            "sm": 2
                        },
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
                    },
                ],
                [
                    {
                        "type": "money",
                        "name": "costo",
                        "label": "Costo",
                        "step": "0.01",
                        "min": "0",
                        "isHorizontal": true,
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 4
                        },
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
                    },
                ],
                [
                    {
                        "type": "number",
                        "name": "existencias",
                        "label": "Existencias",
                        "min": "0",
                        "step": "0.001",
                        // Sin isDecimal, dynamic-forms parsea los inputs "number" con parseInt
                        // y trunca las cantidades decimales del inventario.
                        "isDecimal": true,
                        "defaultValue": "0",
                        "isHorizontal": true,
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 4
                        },
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
                    },
                ],
                [
                    {
                        "type": "number",
                        "name": "minimo",
                        "label": "Minimo",
                        "min": "0",
                        "step": "0.001",
                        "isDecimal": true,
                        "isHorizontal": true,
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 4
                        },
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
                    },
                ],
                [
                    {
                        "type": "number",
                        "name": "maximo",
                        "label": "Maximo",
                        "min": "0",
                        "step": "0.001",
                        "isDecimal": true,
                        "isHorizontal": true,
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 4
                        },
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
                    },
                ],
                [
                    {
                        "type": SELECT_INPUT_TYPE,
                        "name": "visibilidad",
                        "label": "Visibilidad",
                        "defaultValue": 1,
                        "options": PRODUCT_VISIBILITIES,
                        "isHorizontal": true,
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 4
                        },
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
                    },
                ],
                [
                    {
                        "type": EDITOR_INPUT,
                        "name": "descripcion",
                        "label": "Descripcion Larga",
                        "isHorizontal": true,
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 8
                        },
                    },
                ],
                [
                    {
                        "type": "text",
                        "name": "codigobarras",
                        "label": "Codigo de Barras",
                        "isHorizontal": true,
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 6
                        },
                    },
                ],
                [
                    {
                        "type": SELECT_INPUT_TYPE,
                        "name": "tipo_producto",
                        "label": "Tipo predeterminado de Productos",
                        "options": PRODUCT_TYPES,
                        "isHorizontal": true,
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 4
                        },
                        "validation": Yup.string()
                            .required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR)
                            .test(
                                "cabys-tipo-producto",
                                CABYS_PRODUCT_TYPE_MISMATCH_MESSAGE,
                                function (value) {
                                    return isCabysCoherentWithProductType(this.parent?.codigo_cabys, value);
                                }
                            ),
                    },
                ],
                [
                    {
                        "type": SELECT_INPUT_TYPE,
                        "name": "tipo_impuesto",
                        "label": "Tipo predeterminado de Impuesto",
                        "options": TAXES_TYPES,
                        "isHorizontal": true,
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 6
                        },
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
                    },
                ],
                [
                    {
                        "type": SELECT_INPUT_TYPE,
                        "name": "porcentaje_impuesto",
                        "label": "Porcentaje predeterminado de Impuesto",
                        "options": TAXES_PERCENTAGES,
                        "isHorizontal": true,
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 4
                        },
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
                    },
                ],
                [
                    {
                        "type": SELECT_INPUT_TYPE,
                        "name": "unidad_medida",
                        "label": "Unidad de medida predeterminada",
                        "options": MEASURE_UNITS,
                        "isHorizontal": true,
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 4
                        },
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
                    },
                ],
                [
                    {
                        "type": "checkbox",
                        "name": "exento",
                        "label": "Exenta de Impuestos",
                        "sizing": {
                            "sm": {
                                "offset": 2
                            }
                        }
                    }
                ],
                [
                    {
                        "type": "checkbox",
                        "name": "activo",
                        "label": "Activo",
                        "sizing": {
                            "sm": {
                                "offset": 2
                            }
                        }
                    }
                ],
                [
                    {
                        "type": "money",
                        "name": "precio1",
                        "label": "Precio 1",
                        "step": "0.01",
                        "min": "0",
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 3
                        }
                    },
                    {
                        "type": "money",
                        "name": "precio2",
                        "label": "Precio 2",
                        "step": "0.01",
                        "min": "0",
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 3
                        }
                    },
                    {
                        "type": "money",
                        "name": "precio3",
                        "label": "Precio 3",
                        "step": "0.01",
                        "min": "0",
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 3
                        }
                    },
                    {
                        "type": "money",
                        "name": "precio4",
                        "label": "Precio 4",
                        "step": "0.01",
                        "min": "0",
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 3
                        }
                    },
                ],
                [
                    {
                        "type": FILE_UPLOAD_INPUT,
                        "name": "imagen",
                        "label": "Imagen Producto",
                        "sizing": {
                            "sm": {
                                "size": 6,
                                "offset": 0
                            }
                        },
                        "inputConfig": {
                            "server": FormFileServer(),
                            "maxFiles": 1,
                            "allowMultiple": false,
                            "instantUpload": false,
                        }
                    },
                ]
            ]
        }
    ],
};


export default ProductFormConfig;